import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  X, Loader2, MessageSquare, History, Phone, User, Building2,
  Headphones, IndianRupee, CalendarClock, Stethoscope, ExternalLink,
  MessageCircle, MapPin, Mail, Home, Save, PhoneCall,
} from 'lucide-react';
import { dispositionMeta, DISPOSITION_ORDER } from '@/lib/dispositions';
import { TICKETS } from '@/constants/testIds';
import { useAuth } from '@/lib/auth';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';
import CallPatientDialog from '@/components/calls/CallPatientDialog';
import { RemarksLog } from '@/components/tickets/RemarksLog';
import { TicketFilesPanel } from '@/components/tickets/TicketFilesPanel';

const ADMIN_ROLES = new Set(['super_admin', 'ceo', 'central_team']);

function cleanComment(html) {
  if (!html) return '';
  return html
    .replace(/&nbsp;/g, ' ').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/<br\s*\/?>/gi, '\n').replace(/<\/?p\b[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n').trim();
}

export default function TicketDrawer({ ticketId, lookups, users, employees, onClose, onChanged }) {
  const { user } = useAuth();
  const canEditMobile = user && ADMIN_ROLES.has(user.role);
  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [masters, setMasters] = useState({ sources: [], subsources: [], consultants: [], items: [], appointment_types: [], audiologists: [], assignees: [] });

  const [activeTab, setActiveTab] = useState('details');
  const prevDisposition = useRef(null);

  // Auto-jump to Device & Sale tab when disposition moves into a sales stage —
  // reminds the user to capture item / price / invoice while the intent is fresh.
  useEffect(() => {
    if (!ticket) return;
    const salesDispositions = ['sold', 'on_trial', 'appt_booked', 'booked'];
    if (
      prevDisposition.current &&
      prevDisposition.current !== ticket.disposition &&
      salesDispositions.includes(ticket.disposition) &&
      !ticket.item_name
    ) {
      setActiveTab('device');
      toast.info('Capture device details for this sale', { duration: 3500 });
    }
    prevDisposition.current = ticket.disposition;
  }, [ticket]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [t, a] = await Promise.all([
        api.get(`/tickets/${ticketId}`),
        api.get(`/tickets/${ticketId}/activities`),
      ]);
      setTicket(t.data); setActivities(a.data);
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="ticket-drawer"] [data-radix-scroll-area-viewport]');
        if (el) el.scrollTop = 0;
      });
    } catch { setLoadError(true); toast.error('Failed to load ticket'); }
    finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [t, c, m] = await Promise.all([
          api.get('/users/team'),
          api.get('/clinics'),
          api.get('/tickets/lookups/masters'),
        ]);
        setTeam(t.data.data || []);
        setClinics(c.data || []);
        setMasters(m.data || masters);
      } catch { /* non-fatal — masters can be reloaded next time */ }
    })();
  }, []);

  // patch: send a single field diff, refresh silently (no toast spam on autosave)
  const patch = useCallback(async (payload, opts = {}) => {
    if (!payload || Object.keys(payload).length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/tickets/${ticketId}`, payload);
      setTicket(data);
      if (opts.toast !== false) toast.success('Saved');
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  }, [ticketId, onChanged]);

  if (loadError && !loading) {
    return (
      <aside className="w-[600px] shrink-0 border-l border-stone-200 bg-white flex flex-col items-center justify-center gap-2" data-testid={TICKETS.drawer}>
        <div className="text-sm text-stone-600" data-testid="ticket-drawer-error">Couldn't load this patient.</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="ticket-drawer-retry">Retry</Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </aside>
    );
  }

  if (loading || !ticket) {
    return (
      <aside className="w-[600px] shrink-0 border-l border-stone-200 bg-white flex items-center justify-center" data-testid={TICKETS.drawer}>
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </aside>
    );
  }

  const m = dispositionMeta(ticket.disposition);
  const Icon = m.icon;
  const commentClean = cleanComment(ticket.comment);
  const manual = new Set(ticket.manual_fields || []);

  return (
    <aside
      className="w-[600px] shrink-0 border-l border-stone-200 bg-white flex flex-col shadow-xl"
      data-testid={TICKETS.drawer}
    >
      {/* Header */}
      <div className="p-3 border-b border-stone-200 bg-stone-50 flex items-start gap-2">
        <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: m.text }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: m.bg, color: m.text, border: `1px solid ${m.border}` }}>
              <Icon className="w-2.5 h-2.5" /> {m.label}
            </span>
            <span className="text-[10px] text-stone-500 font-mono">{ticket.ticket_no}</span>
            {ticket.fundle_ticket_id && (
              <span className="text-[10px] text-stone-400 font-mono">fundle:{ticket.fundle_ticket_id}</span>
            )}
            {ticket.clinic_source === 'assignee_hint' && (
              <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-1 py-0.5 rounded font-semibold">clinic auto-corrected</span>
            )}
            {saving && <Loader2 className="w-3 h-3 animate-spin text-stone-400" />}
          </div>
          <div className="font-heading text-lg font-bold text-stone-900 truncate mt-1">
            {ticket.patient_name || ticket.subject || 'Untitled'}
          </div>
          <div className="text-xs text-stone-600 flex items-center gap-3 mt-0.5">
            {ticket.patient_mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {ticket.patient_mobile}</span>}
            {ticket.clinic_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {ticket.clinic_name}</span>}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7" data-testid={TICKETS.drawerClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="px-3 py-1.5 border-b border-stone-200 bg-white flex items-center justify-end gap-2">
        <Button
          variant="outline" size="sm"
          className="h-7 text-xs border-rose-200 text-rose-800 hover:bg-rose-50"
          onClick={() => setCallOpen(true)}
          disabled={!ticket.patient_mobile}
          data-testid="ticket-call-patient-btn"
        >
          <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call Patient (AI)
        </Button>
        <Button
          variant="outline" size="sm"
          className="h-7 text-xs border-emerald-200 text-emerald-800 hover:bg-emerald-50"
          onClick={() => setWaOpen(true)}
          disabled={!ticket.patient_mobile}
          data-testid="ticket-wa-send-btn"
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Send WhatsApp
        </Button>
      </div>

      <WhatsAppSendDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        to={ticket.patient_mobile || ''}
        patientName={ticket.patient_name || ''}
        ticketId={ticketId}
        onSent={() => load()}
      />

      <CallPatientDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        ticketId={ticketId}
        patientName={ticket.patient_name || ''}
        patientMobile={ticket.patient_mobile || ''}
        onInitiated={() => load()}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="grid grid-cols-6 mx-3 mt-2 h-8 shrink-0">
          <TabsTrigger value="details" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('summary')}>Details</TabsTrigger>
          <TabsTrigger value="schedule" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('schedule')}>Scheduling</TabsTrigger>
          <TabsTrigger value="device" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('device')}>Device & Sale</TabsTrigger>
          <TabsTrigger value="contact" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('contact')}>Contact</TabsTrigger>
          <TabsTrigger value="notes" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('notes')}>Remarks ({activities.filter(a=>a.kind==='note').length})</TabsTrigger>
          <TabsTrigger value="files" className="text-[10px] uppercase tracking-wider" data-testid={TICKETS.drawerTab('files')}>Files</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1 hc-scrollbar">
          {/* ---- Details ---- */}
          <TabsContent value="details" className="p-3 space-y-3 mt-0">
            <Section title="Disposition & Assignment" icon={User}>
              <Row label="Status">
                <Select value={ticket.disposition} onValueChange={(v) => patch({ disposition: v })} disabled={saving}>
                  <SelectTrigger className="h-8" data-testid={TICKETS.changeDispositionSelect}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {DISPOSITION_ORDER.map((k) => {
                      const dm = dispositionMeta(k);
                      return (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-1.5"><dm.icon className="w-3 h-3" style={{ color: dm.text }} />{dm.label}</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Assignee" edited={manual.has('assignee_name')}>
                <Select
                  value={ticket.assignee_id || ticket.assignee_email || 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') patch({ assignee_id: null, assignee_name: null });
                    else {
                      const mem = team.find(x => (x.id && x.id === v) || x.email === v);
                      patch({ assignee_id: mem?.id || null, assignee_name: mem?.name || null });
                    }
                  }}
                  disabled={saving}
                >
                  <SelectTrigger className="h-8" data-testid={TICKETS.changeAssigneeSelect}>
                    <SelectValue placeholder={ticket.assignee_name || 'Unassigned'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {team.map((mem) => (
                      <SelectItem key={mem.id || mem.email} value={mem.id || mem.email}>
                        <span className="flex items-center gap-1"><span>{mem.name}</span><span className="text-[9px] text-stone-500">{mem.role_label || mem.fundle_role || ''}</span></span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Row>
            </Section>

            <Section title="Lead & Clinic" icon={Building2}>
              <Row label="Source" edited={manual.has('source_name')}>
                <ComboSelect value={ticket.source_name} options={masters.sources} onChange={(v) => patch({ source_name: v })} placeholder="Select source…" />
              </Row>
              <Row label="Sub-source" edited={manual.has('subsource_name')}>
                <ComboSelect value={ticket.subsource_name} options={masters.subsources} onChange={(v) => patch({ subsource_name: v })} placeholder="Select sub-source…" />
              </Row>
              <Row label="Clinic" edited={manual.has('clinic_name')}>
                <Select
                  value={ticket.clinic_id || ''}
                  onValueChange={(v) => {
                    const c = clinics.find(x => x.id === v);
                    patch({ clinic_id: v, clinic_name: c?.name || null });
                  }}
                  disabled={saving}
                >
                  <SelectTrigger className="h-8"><SelectValue placeholder={ticket.clinic_name || 'Select clinic…'} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Consultant" edited={manual.has('consultant_name')}>
                <ComboSelect value={ticket.consultant_name} options={masters.consultants} onChange={(v) => patch({ consultant_name: v })} placeholder="Search consultant…" leftIcon={Stethoscope} />
              </Row>
              <Row label="Audiologist" edited={manual.has('audiologist_name')}>
                <ComboSelect
                  value={ticket.audiologist_name}
                  options={(masters.audiologists || []).map(a => a.name).filter(Boolean)}
                  onChange={(v) => {
                    const a = (masters.audiologists || []).find(x => x.name === v);
                    patch({ audiologist_name: v, audiologist_email: a?.email || null });
                  }}
                  placeholder="Search audiologist…"
                />
              </Row>
            </Section>
          </TabsContent>

          {/* ---- Scheduling ---- */}
          <TabsContent value="schedule" className="p-3 space-y-3 mt-0">
            <Section title="Appointment" icon={CalendarClock}>
              <Row label="Type" edited={manual.has('appointment_type_name')}>
                <ComboSelect value={ticket.appointment_type_name} options={masters.appointment_types} onChange={(v) => patch({ appointment_type_name: v })} placeholder="Select type…" />
              </Row>
              <Row label="Appointment" edited={manual.has('appointment_ts')}>
                <DateTimeField value={ticket.appointment_ts} onSave={(v) => patch({ appointment_ts: v })} />
              </Row>
              <Row label="Follow-up" edited={manual.has('followup_date')}>
                <DateField value={ticket.followup_date} onSave={(v) => patch({ followup_date: v })} />
              </Row>
            </Section>
          </TabsContent>

          {/* ---- Device & Sale ---- */}
          <TabsContent value="device" className="p-3 space-y-3 mt-0">
            <Section title="Device / Item" icon={Headphones}>
              <Row label="Item" edited={manual.has('item_name')}>
                <ComboSelect value={ticket.item_name} options={masters.items} onChange={(v) => patch({ item_name: v })} placeholder="Search device SKU…" />
              </Row>
              <Row label="Device pitched" edited={manual.has('device_sold')}>
                <TextField value={ticket.device_sold} onSave={(v) => patch({ device_sold: v || null })} placeholder="e.g. Phonak Naida L 30" />
              </Row>
            </Section>
            <Section title="Financials" icon={IndianRupee}>
              <Row label="Price offered ₹" edited={manual.has('price')}>
                <TextField value={ticket.price} onSave={(v) => patch({ price: v || null })} placeholder="0" inputMode="numeric" />
              </Row>
              <Row label="Final selling ₹" edited={manual.has('final_selling_price')}>
                <TextField value={ticket.final_selling_price} onSave={(v) => patch({ final_selling_price: v || null })} placeholder="0" inputMode="numeric" />
              </Row>
              <Row label="Diagnostics ₹" edited={manual.has('diagnostics_amount')}>
                <TextField value={ticket.diagnostics_amount} onSave={(v) => patch({ diagnostics_amount: v || null })} placeholder="0" inputMode="numeric" />
              </Row>
              <Row label="Invoice #" edited={manual.has('invoice_no')}>
                <TextField value={ticket.invoice_no} onSave={(v) => patch({ invoice_no: v || null })} placeholder="INV-…" />
              </Row>
            </Section>
          </TabsContent>

          {/* ---- Contact ---- */}
          <TabsContent value="contact" className="p-3 space-y-3 mt-0">
            <Section title="Patient" icon={User}>
              <Row label="Name" edited={manual.has('patient_name')}>
                <TextField value={ticket.patient_name} onSave={(v) => patch({ patient_name: v || null })} placeholder="Full name" />
              </Row>
              <Row label="Mobile" edited={manual.has('patient_mobile')}>
                <TextField
                  value={ticket.patient_mobile}
                  onSave={(v) => patch({ patient_mobile: v || null })}
                  placeholder="10-digit"
                  leftIcon={Phone}
                  readOnly={!canEditMobile}
                  readOnlyHint="Only Super Admin / CEO can edit patient mobile"
                />
              </Row>
              <Row label="Email" edited={manual.has('patient_email')}>
                <TextField value={ticket.patient_email} onSave={(v) => patch({ patient_email: v || null })} placeholder="email@…" leftIcon={Mail} />
              </Row>
            </Section>
            <Section title="Address" icon={MapPin}>
              <Row label="Address" edited={manual.has('patient_address')}>
                <TextareaField value={ticket.patient_address} onSave={(v) => patch({ patient_address: v || null })} placeholder="House / street / city / pincode" />
              </Row>
              <Row label="Location link" edited={manual.has('location_link')}>
                <TextField value={ticket.location_link} onSave={(v) => patch({ location_link: v || null })} placeholder="https://maps.google.com/…" leftIcon={Home} />
              </Row>
              {ticket.location_link && (
                <div className="pl-[110px]">
                  <a href={ticket.location_link} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Open in Maps
                  </a>
                </div>
              )}
            </Section>
          </TabsContent>

          {/* ---- Notes / Activity combined ---- */}
          <TabsContent value="notes" className="p-3 mt-0">
            <RemarksLog ticketId={ticketId} activities={activities} fundleComment={commentClean} onAdded={load} />
          </TabsContent>

          <TabsContent value="files" className="p-3 mt-0">
            <TicketFilesPanel ticketId={ticketId} />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </aside>
  );
}

/* ---------- Auto-save primitives ---------- */

function TextField({ value, onSave, placeholder, leftIcon: LeftIcon, inputMode, readOnly, readOnlyHint }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div className="flex items-center gap-1">
      {LeftIcon && <LeftIcon className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
      <Input
        value={v}
        onChange={(e) => !readOnly && setV(e.target.value)}
        onBlur={() => { if (!readOnly && v !== (value || '')) onSave(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder={placeholder}
        inputMode={inputMode}
        readOnly={readOnly}
        title={readOnly ? (readOnlyHint || 'Read-only') : undefined}
        className={`h-8 text-sm ${readOnly ? 'bg-stone-50 cursor-not-allowed text-stone-700' : ''}`}
      />
    </div>
  );
}

function TextareaField({ value, onSave, placeholder }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <Textarea
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== (value || '')) onSave(v); }}
      placeholder={placeholder}
      rows={2}
      className="text-sm resize-none"
    />
  );
}

function DateField({ value, onSave }) {
  // value is ISO string; convert to YYYY-MM-DD for input
  const iso = value ? new Date(value).toISOString().slice(0, 10) : '';
  const [v, setV] = useState(iso);
  useEffect(() => { setV(iso); }, [iso]);
  return (
    <Input
      type="date"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v && v !== iso) onSave(new Date(v + 'T00:00:00').toISOString());
        else if (!v && iso) onSave(null);
      }}
      className="h-8 text-sm"
    />
  );
}

function DateTimeField({ value, onSave }) {
  // yyyy-MM-ddTHH:mm
  const local = value ? new Date(value).toISOString().slice(0, 16) : '';
  const [v, setV] = useState(local);
  useEffect(() => { setV(local); }, [local]);
  return (
    <Input
      type="datetime-local"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v && v !== local) onSave(new Date(v).toISOString());
        else if (!v && local) onSave(null);
      }}
      className="h-8 text-sm"
    />
  );
}

/* Combo dropdown with client-side search — Fundle masters can be thousands */
function ComboSelect({ value, options, onChange, placeholder, leftIcon: LeftIcon }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    const arr = (options || []).filter(x => !!x);
    if (!norm) return arr.slice(0, 200);
    return arr.filter(o => (o || '').toLowerCase().includes(norm)).slice(0, 200);
  }, [q, options]);
  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex items-center gap-1">
        {LeftIcon && <LeftIcon className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
        <Input
          value={open ? q : (value || '')}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setQ(''); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filtered.length > 0) { onChange(filtered[0]); setOpen(false); }
            if (e.key === 'Escape') setOpen(false);
          }}
          className="h-8 text-sm"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-0.5 w-full max-h-56 overflow-auto bg-white border border-stone-200 rounded shadow-lg">
          {filtered.length === 0 && <div className="px-2 py-1.5 text-xs text-stone-400 italic">No matches</div>}
          {value && (
            <button
              className="w-full text-left px-2 py-1.5 text-xs text-red-700 hover:bg-red-50 border-b border-stone-100"
              onClick={() => { onChange(null); setOpen(false); }}
            >× Clear</button>
          )}
          {filtered.map((o) => (
            <button key={o} className={`w-full text-left px-2 py-1.5 text-xs hover:bg-stone-50 ${o === value ? 'bg-indigo-50 font-semibold' : ''}`}
              onClick={() => { onChange(o); setOpen(false); }}>{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-stone-200 rounded-md">
      <div className="px-3 py-1.5 border-b border-stone-100 bg-stone-50 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-stone-500" />
        <span className="text-[10px] uppercase tracking-widest text-stone-600 font-semibold">{title}</span>
      </div>
      <div className="px-3 py-2 space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, edited, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 items-center text-sm">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1">
        {label}
        {edited && <span title="Manually edited — Fundle sync will not overwrite" className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
      </div>
      <div className="text-stone-800 min-w-0">{children}</div>
    </div>
  );
}
